import React, { useCallback, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Grid } from '@mui/material';
import { Box, Button, Chip, DateTimeStandard, MenuItem, SelectField, Stack, Switch, TextField } from '@curves/ui-base';
import { OverflowMenu, Overview } from '@curves/ui-shared';

import { PageLayout } from '@Local/Components/Layout';
import { filterMenu } from '@Local/Orders/Orders.util';
import { ensureArray, isEmpty } from '@Util/utils';
import useOverview from '@Util/hook/useOverview';
import useOptions from '@Util/hook/useOptions';
import useMessageDialog from '@Util/hook/useMessageDialog';
import { displayDateText, parseDate } from '@Util/moment.util';
import useCampaignApi from '@Apis/useCampaignApi';
import { CAMPAIGN_ADD_ROUTER, CAMPAIGN_EDIT_ROUTER_URL } from '@Router/routerPath';
import { formatDiscountText } from './CampaignEdit.config';

// 搜尋預設值（發行來源、上下架的 0 代表全部）
const defaultSearchParams = {
    pageIndex: 1,
    pageSize: 20,
    keyword: '',
    sourceType: '0',
    isEnabled: '0',
};

// 每列更多操作選單：編輯一律可用，刪除只限未被訂單使用的券
const getCampaignMenuMap = ({ campaignID, isUsed }, { onDelete }) => [
    {
        text: '編輯',
        condition: true,
        component: Link,
        to: CAMPAIGN_EDIT_ROUTER_URL(campaignID),
    },
    {
        text: '刪除',
        condition: !isUsed,
        onClick: () => onDelete(campaignID),
    },
];

// 無值時的佔位顯示
const EmptyMark = () => <span className="font-color-3">—</span>;

// 總覽表格欄位，選項與操作行為由 cellRenderContext 傳入
const headerRow = [
    {
        cellKey: 'campaignName',
        headerLabel: '名稱',
        minWidth: 180,
        formatCell: (campaignName, { campaignID }) => <Link to={CAMPAIGN_EDIT_ROUTER_URL(campaignID)}>{campaignName}</Link>,
    },
    {
        cellKey: 'sourceTypeText',
        headerLabel: '發行來源',
        minWidth: 80,
    },
    {
        cellKey: 'discountValue',
        headerLabel: '折扣',
        minWidth: 100,
        formatCell: (_, row) => formatDiscountText(row),
    },
    {
        cellKey: 'isProductLimit',
        headerLabel: '商品限制',
        align: 'center',
        formatCell: isProductLimit => (isProductLimit ? <Chip size="small" color="primary" label="有限制" /> : <EmptyMark />),
    },
    {
        cellKey: 'startDate',
        headerLabel: '使用期間',
        minWidth: 180,
        formatCell: (startDate, { endDate }) => displayDateText(startDate, endDate),
    },
    {
        cellKey: 'isUsed',
        headerLabel: '已使用',
        align: 'center',
        formatCell: isUsed => (isUsed ? <Chip size="small" color="gray" label="已使用" /> : <EmptyMark />),
    },
    {
        cellKey: 'isEnabled',
        headerLabel: '是否上架',
        align: 'center',
        formatCell: (isEnabled, { campaignID }, { onChangeEnabled }) => (
            <Switch checked={Boolean(isEnabled)} onChange={(_, checked) => onChangeEnabled(campaignID, Boolean(checked))} />
        ),
    },
    {
        cellKey: 'updatedDate',
        headerLabel: '最後修改',
        minWidth: 130,
        formatCell: (updatedDate, { updaterName }) => (
            <Box>
                <Box>{updatedDate ? parseDate(updatedDate, DateTimeStandard.DisplayDateBySlash) : ''}</Box>
                <Box className="font-color-3">{updaterName}</Box>
            </Box>
        ),
    },
    {
        cellKey: 'more',
        headerLabel: '',
        width: 6.8,
        formatCell: (_, rowData, { onDelete }) => {
            const menuData = filterMenu(getCampaignMenuMap(rowData, { onDelete }));
            return <OverflowMenu menuData={menuData} enabled={!isEmpty(menuData)} />;
        },
    },
];

/**
 * HQ 抵用券總覽
 * - 可依名稱、發行來源、上下架搜尋並分頁
 * - 可切換上下架（上架、下架都需確認）、刪除未使用的券，並導向新增與編輯頁
 */
const CampaignOverviewPage = () => {
    const history = useHistory();
    const { postCampaignOverviewApi, getCampaignOptionsApi, putCampaignEnableApi, deleteCampaignApi } = useCampaignApi();
    const setMessageDialog = useMessageDialog();
    // 取得搜尋列選項（發行來源、上下架含「全部」）
    const getOptionsApi = useCallback(() => getCampaignOptionsApi({ skipAll: false }), [getCampaignOptionsApi]);
    // 抵用券選項（發行來源、上下架、基準價）
    const { options } = useOptions({ getOptionsApi, isFetchOnMount: true });
    const { searchParams, overviewData, getOverviewData, updateOverviewPage } = useOverview({
        getOverviewApi: postCampaignOverviewApi,
        defaultSearchParams,
        isFetchOnMount: true,
        enableQueryString: true,
    });

    // 頁首右側操作按鈕
    const pageActions = [{ label: '新增抵用券', variant: 'contained', onClick: () => history.push(CAMPAIGN_ADD_ROUTER) }];

    // 搜尋欄位的 ref，送出時取值
    const keywordRef = useRef(null);
    const sourceTypeRef = useRef(null);
    const isEnabledRef = useRef(null);

    // 送出搜尋：回傳查詢參數交給 Overview.Search 查詢，條件改變回到第一頁
    const handleSubmit = () => ({
        ...searchParams,
        keyword: keywordRef.current?.getResult() || '',
        sourceType: sourceTypeRef.current?.getResult() ?? '0',
        isEnabled: isEnabledRef.current?.getResult() ?? '0',
        pageIndex: 1,
    });

    // 重新載入目前條件的總覽
    const refresh = () => getOverviewData(searchParams);

    // 呼叫上下架 API，成功後重新載入
    const updateEnabled = async (campaignID, enabled) => {
        const resp = await putCampaignEnableApi(campaignID, enabled);
        if (resp) refresh();
    };

    // 切換上下架：兩個方向都先開確認彈窗，關閉彈窗則開關維持原值
    const handleChangeEnabled = (campaignID, enabled) => {
        setMessageDialog({
            open: true,
            title: '上下架確認',
            msg: enabled ? '確認要上架這張抵用券嗎？' : '確認要下架這張抵用券嗎？下架後會員將無法使用。',
            buttonText: enabled ? '確認上架' : '確認下架',
            onConfirm: () => updateEnabled(campaignID, enabled),
        });
    };

    // 刪除前確認，成功後重新載入
    const handleDelete = campaignID => {
        setMessageDialog({
            open: true,
            title: '刪除抵用券',
            msg: '刪除後無法復原，確定要刪除這張抵用券嗎？',
            onConfirm: async () => {
                const resp = await deleteCampaignApi(campaignID);
                if (resp) refresh();
            },
        });
    };

    return (
        <PageLayout>
            <PageLayout.Header title="抵用券管理" actions={pageActions} />
            <Overview contextValue={{ overviewData, searchParams, getOverviewData, updateOverviewPage }} idKey="campaignID">
                <Overview.Search className="p-3 mb-1" getSearchParamsOnSubmit={handleSubmit}>
                    <Grid spacing={2} container>
                        <Grid xs={12} md={6} item>
                            <TextField
                                label="名稱"
                                ref={keywordRef}
                                key={searchParams?.keyword}
                                defaultValue={searchParams?.keyword}
                                InputProps={{ placeholder: '請輸入抵用券名稱關鍵字' }}
                                maxLength={50}
                                fullWidth
                            />
                        </Grid>
                        <Grid xs={6} md={3} item>
                            <SelectField
                                label="發行來源"
                                ref={sourceTypeRef}
                                key={`${searchParams?.sourceType}_${ensureArray(options?.sourceTypes).length}`}
                                defaultValue={searchParams?.sourceType}
                                fullWidth
                            >
                                {ensureArray(options?.sourceTypes).map(item => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.text}
                                    </MenuItem>
                                ))}
                            </SelectField>
                        </Grid>
                        <Grid xs={6} md={3} item>
                            <SelectField
                                label="上下架"
                                ref={isEnabledRef}
                                key={`${searchParams?.isEnabled}_${ensureArray(options?.isEnabledOptions).length}`}
                                defaultValue={searchParams?.isEnabled}
                                fullWidth
                            >
                                {ensureArray(options?.isEnabledOptions).map(item => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.text}
                                    </MenuItem>
                                ))}
                            </SelectField>
                        </Grid>
                    </Grid>
                    <Stack className="mt-2" justifyContent="end" spacing={2}>
                        <Button variant="contained" color="primary" type="submit">
                            搜尋
                        </Button>
                    </Stack>
                </Overview.Search>
                <Stack className="overview__toolbar" alignItems="center" justifyContent="end">
                    <Overview.TotalCount showRange />
                </Stack>
                <Overview.Table
                    headerRow={headerRow}
                    cellRenderContext={{
                        onChangeEnabled: handleChangeEnabled,
                        onDelete: handleDelete,
                    }}
                />
                <Overview.Pagination className="mt-4" />
            </Overview>
        </PageLayout>
    );
};

export default CampaignOverviewPage;
